<script lang="ts">
  import { untrack } from 'svelte';
  import QueryStatus from '$lib/components/ui/QueryStatus.svelte';
  import { formatDate, todayIST } from '$lib/utils/dates';
  import { scheduleUndo } from '$lib/state/actionDialog.svelte';
  import { route } from '$lib/state/route.svelte';
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Trash2, Globe, Upload, MapPin, ChevronRight } from 'lucide-svelte';
  import { peopleApi, companiesApi, companiesLookupApi } from '$lib/api/crm';
  import { adminTrackedDomainsApi } from '$lib/api/admin';
  import { session } from '$lib/state/session.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import { getErrorMessage } from '$lib/api/client';
  import { isHotelsVertical } from '$lib/utils/verticals';
  import SearchBar from '$lib/components/ui/SearchBar.svelte';
  import Pagination from '$lib/components/ui/Pagination.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import CustomFieldsForm from '$lib/components/ui/CustomFieldsForm.svelte';
  import CompanyCombobox from '$lib/components/ui/CompanyCombobox.svelte';
  import ImportWizard from '$lib/components/ImportWizard.svelte';
  import ContactHistory from './ContactHistory.svelte';
  import ContactOwnership from './ContactOwnership.svelte';
  import CoordinatorWorkspace from './CoordinatorWorkspace.svelte';
  import { contactOwnerLabel } from '$lib/utils/contactOwnership';
  import type { CompanyLookup, Person } from '$lib/types';

  let showImport = $state(false);

  // Free/public email providers a tracked domain must never be — mirrors
  // handlers.blockedEmailDomains (internal/api/handlers/admin_tracked_domains.go).
  // Duplicated rather than fetched: it's a small, rarely-changing list and
  // this is only a client-side pre-filter for the track-domain suggestion
  // below — the server is still the source of truth and would 400 on a
  // false negative here.
  const FREE_EMAIL_DOMAINS = new Set([
    'gmail.com',
    'googlemail.com',
    'hotmail.com',
    'outlook.com',
    'live.com',
    'icloud.com',
    'aol.com',
    'proton.me',
    'protonmail.com',
    'rediffmail.com',
    'zoho.com'
  ]);
  function isFreeEmailDomain(domain: string): boolean {
    return FREE_EMAIL_DOMAINS.has(domain) || domain === 'yahoo.com' || domain.startsWith('yahoo.');
  }
  function emailDomain(email: string): string {
    return email.trim().toLowerCase().split('@')[1] ?? '';
  }

  const queryClient = useQueryClient();

  let search = $state('');
  let category = $state('');
  let ownerFilter = $state('');
  let historyPerson = $state<Person | null>(null);
  const selectedId = $derived(route.current.tab === 'people' ? route.current.id : undefined);
  const recordQuery = createQuery(() => ({
    queryKey: ['person', selectedId], queryFn: () => peopleApi.get(selectedId!), enabled: selectedId !== undefined
  }));
  $effect(() => {
    if (recordQuery.data && recordQuery.data.id === selectedId) historyPerson = recordQuery.data;
    else if (!selectedId) historyPerson = null;
  });
  function openHistory(person: Person) {
    historyPerson = person;
    route.push({ tab: 'people', id: person.id });
  }
  function closeHistory() {
    historyPerson = null;
    if (selectedId) route.push({ tab: 'people' });
  }
  function contactTransferred() {
    historyPerson = null;
    // The handover is already saved; do not ask to discard its now-stale form
    // or keep a deep link to a record whose pipeline may be inaccessible.
    route.replace({ tab: 'people' });
  }
  const categories = [
    ['architect', 'Architect'], ['interior_designer', 'Interior designer'],
    ['lighting_vendor', 'Lighting vendor'], ['lighting_consultant', 'Lighting consultant'],
    ['pmc', 'PMC'], ['electrical_contractor', 'Electrical contractor'],
    ['homeowner', 'Homeowner'], ['other', 'Other']
  ];
  function categoryLabel(value: string) {
    return categories.find(([key]) => key === value)?.[1] ?? 'Uncategorized';
  }
  function dateLabel(value: string | null | undefined) {
    return formatDate(value);
  }
  function followupDateLabel(value: string | null | undefined) {
    if (!value) return 'No date set';
    const date = value.slice(0, 10);
    const today = todayIST();
    return `${date < today ? 'Overdue' : date === today ? 'Today' : 'Upcoming'} · ${formatDate(value)}`;
  }
  function followupClass(value: string | null | undefined) {
    const date = value?.slice(0, 10);
    const today = todayIST();
    return date && date < today ? 'border-red-200 bg-red-50 text-red-700' : date === today ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-teal-200 bg-teal-50 text-teal-800';
  }
  function interactionLabel(value: string) {
    return value.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
  }
  let page = $state(1);
  let showForm = $state(false);
  let editingId = $state<number | null>(null);
  let formError = $state('');
  // "Unlinked" filter (Hotels only) — finds contacts with no company link at
  // all (see the ?unlinked= filter on GET /people) without anyone needing to
  // go spelunking in SQL for them, which is exactly how a batch of Hotels
  // contacts went unnoticed before.
  let unlinkedOnly = $state(false);

  const verticalId = $derived(
    session.selectedVertical === 'all' ? undefined : session.selectedVertical
  );
  // The Hotels vertical replaces the generic company picker with three
  // separate hierarchy pickers (Hotel/Brand/Group) — see isHotelsVertical's
  // doc and models.Person (internal/models/crm.go) for the company_id
  // derivation rule the backend applies from these.
  const listIsHotels = $derived(isHotelsVertical(verticalId, session.me?.verticals));

  function emptyForm() {
    return {
      vertical_id: verticalId ?? session.me?.verticals[0]?.id,
      company_id: undefined as number | undefined,
      hotel_company_id: undefined as number | undefined,
      brand_company_id: undefined as number | undefined,
      owner_company_id: undefined as number | undefined,
      owner_user_id: undefined as number | undefined,
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      whatsapp: '',
      job_title: '',
      city: '',
      relationship_type: '',
      notes: '',
      custom: {} as Record<string, unknown>
    };
  }
  let form = $state(emptyForm());
  const formIsHotels = $derived(isHotelsVertical(form.vertical_id, session.me?.verticals));
  const customFieldDefs = $derived(session.customFieldsFor('people', form.vertical_id));

  const listQuery = createQuery(() => ({
    queryKey: ['people', { search, page, verticalId, category, ownerFilter, unlinkedOnly: listIsHotels && unlinkedOnly }],
    queryFn: () =>
      peopleApi.list({
        q: search || undefined,
        page,
        per_page: 25,
        vertical_id: verticalId,
        relationship_type: category || undefined,
        owner_id: ownerFilter || undefined,
        unlinked: listIsHotels && unlinkedOnly ? 'true' : undefined
      })
  }));
  const createOwnersQuery = createQuery(() => ({
    queryKey: ['contact-owners', form.vertical_id],
    queryFn: () => peopleApi.owners(form.vertical_id!),
    enabled: showForm && !editingId && !!form.vertical_id && (session.isAdmin || !!session.me?.user.intake_coordinator)
  }));
  const filterOwnersQuery = createQuery(() => ({
    queryKey: ['contact-owners', verticalId],
    queryFn: () => peopleApi.owners(verticalId!),
    enabled: verticalId !== undefined
  }));
  $effect(() => {
    verticalId;
    // A named owner selected in another pipeline must not silently survive a
    // scope switch. The cross-pipeline My contacts/Unassigned filters can stay.
    untrack(() => { if (/^[1-9]\d*$/.test(ownerFilter)) ownerFilter = ''; });
  });
  const personDetailQuery = createQuery(() => {
    const id = historyPerson?.id;
    return {
      queryKey: ['person-detail', id],
      queryFn: () => peopleApi.get(id!),
      enabled: id !== undefined
    };
  });
  const detailPerson = $derived(
    personDetailQuery.data?.id === historyPerson?.id ? personDetailQuery.data :
      listQuery.data?.results.find((row) => row.id === historyPerson?.id) ?? historyPerson
  );

  // Lightweight company picker, scoped to the same vertical set as the list —
  // only needed (and only fetched) for the non-Hotels generic Company field.
  const companiesQuery = createQuery(() => ({
    queryKey: ['companies-picker', { verticalId }],
    queryFn: () => companiesApi.list({ per_page: 100, vertical_id: verticalId }),
    enabled: !listIsHotels
  }));

  // Per-kind pools for the Hotels form's three hierarchy pickers — feeds
  // CompanyCombobox's `companies` prop so it can resolve an already-set
  // hotel/brand_company_id/owner_company_id to a display name (edit mode)
  // and so a hotel-picker auto-fill (see onHotelSelect) resolves immediately
  // rather than waiting on a fresh search. Only fetched while the form is
  // open on a Hotels-vertical row (per_page 200, same cap as the lookup
  // endpoint itself — a picker pool, not a paginated list).
  const hotelsPoolQuery = createQuery(() => ({
    queryKey: ['companies-picker-hotel', { verticalId: form.vertical_id }],
    queryFn: () => companiesLookupApi.list({ kind: 'hotel', vertical_id: form.vertical_id, per_page: 200 }),
    enabled: showForm && formIsHotels
  }));
  const brandsPoolQuery = createQuery(() => ({
    queryKey: ['companies-picker-brand', { verticalId: form.vertical_id }],
    queryFn: () => companiesLookupApi.list({ kind: 'brand', vertical_id: form.vertical_id, per_page: 200 }),
    enabled: showForm && formIsHotels
  }));
  const ownersPoolQuery = createQuery(() => ({
    queryKey: ['companies-picker-owner', { verticalId: form.vertical_id }],
    queryFn: () => companiesLookupApi.list({ kind: 'owner', vertical_id: form.vertical_id, per_page: 200 }),
    enabled: showForm && formIsHotels
  }));

  // Picking a hotel auto-fills Brand/Group from its own brand/owner links
  // (companyLookupJSON's brand_company_id/owner_company_id, additive on
  // kind=hotel rows) — but only into EMPTY fields, never overwriting a
  // user's explicit choice, and both stay editable/clearable afterward.
  function onHotelSelect(c: CompanyLookup) {
    if (form.brand_company_id === undefined && c.brand_company_id) {
      form.brand_company_id = c.brand_company_id;
    }
    if (form.owner_company_id === undefined && c.owner_company_id) {
      form.owner_company_id = c.owner_company_id;
    }
  }

  $effect(() => {
    search;
    category;
    ownerFilter;
    verticalId;
    unlinkedOnly;
    page = 1;
  });

  // Reset the filter when it's not applicable (leaving a non-Hotels vertical)
  // so it doesn't silently apply again if the user switches back later.
  $effect(() => {
    if (!listIsHotels) unlinkedOnly = false;
  });

  // Guards against a stale hierarchy selection surviving an in-form vertical
  // switch away from Hotels (only reachable by an admin with more than one
  // accessible vertical — see the vertical <select> below): the fields
  // aren't rendered/editable once formIsHotels goes false, so leaving them
  // set would silently send an unrelated vertical's hotel/brand/owner link
  // on submit. company_id itself is untouched here — same as before this
  // feature, the visible Company field is what the user is expected to redo.
  $effect(() => {
    if (formIsHotels || (!form.hotel_company_id && !form.brand_company_id && !form.owner_company_id)) return;
    form.hotel_company_id = undefined;
    form.brand_company_id = undefined;
    form.owner_company_id = undefined;
  });

  // ===================== Track-domain suggestion (admin only, feature 3) =====================
  // Shares its query key with Admin > Tracked Domains (TrackedDomainsPage) so
  // a recent visit there is reused rather than re-fetched.
  const adminDomainsQuery = createQuery(() => ({
    queryKey: ['admin-tracked-domains'],
    queryFn: () => adminTrackedDomainsApi.list(),
    enabled: session.isAdmin
  }));
  let trackSuggestion = $state<
    { domain: string; companyId: number; companyName: string; verticalId: number } | null
  >(null);
  let trackSuggestionError = $state('');

  // Dismissible, never blocks the save (save has already succeeded by the
  // time this runs) — admin-only since the tracked-domains endpoint is
  // admin-gated anyway.
  function maybeSuggestTrackDomain(person: Person) {
    if (!session.isAdmin || !person.email || !person.company_id) return;
    const domain = emailDomain(person.email);
    if (!domain || isFreeEmailDomain(domain)) return;
    if (adminDomainsQuery.data?.results.some((d) => d.domain === domain)) return;
    trackSuggestionError = '';
    trackSuggestion = {
      domain,
      companyId: person.company_id,
      // companiesQuery isn't fetched for a Hotels-vertical save (its picker
      // is the hierarchy fields instead — see listIsHotels) — fall back to
      // the derived company_id's own name straight off the saved person.
      companyName:
        companyName(person.company_id) ||
        person.hotel_name ||
        person.brand_name ||
        person.owner_name ||
        'this company',
      verticalId: person.vertical_id
    };
  }

  const trackDomainMutation = createMutation(() => ({
    mutationFn: () =>
      adminTrackedDomainsApi.create({
        domain: trackSuggestion?.domain,
        vertical_id: trackSuggestion?.verticalId,
        company_id: trackSuggestion?.companyId
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tracked-domains'] });
      trackSuggestion = null;
    },
    onError: (err: unknown) => (trackSuggestionError = getErrorMessage(err))
  }));

  const saveMutation = createMutation(() => ({
    mutationFn: (body: Record<string, unknown>) =>
      editingId ? peopleApi.update(editingId, body) : peopleApi.create(body),
    onSuccess: (person) => {
      queryClient.invalidateQueries({ queryKey: ['people'] });
      queryClient.invalidateQueries({ queryKey: ['person-detail', person.id] });
      toast.success(editingId ? 'Contact updated.' : 'Contact created. Open the contact to log your first interaction.');
      closeForm();
      maybeSuggestTrackDomain(person);
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  const deleteMutation = createMutation(() => ({
    mutationFn: (id: number) => peopleApi.remove(id),
    onError: (err: unknown) => toast.error(getErrorMessage(err)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['people'] })
  }));

  function openCreate() {
    editingId = null;
    form = emptyForm();
    formError = '';
    showForm = true;
  }

  function openEdit(row: Person) {
    editingId = row.id;
    form = {
      vertical_id: row.vertical_id,
      company_id: row.company_id ?? undefined,
      hotel_company_id: row.hotel_company_id ?? undefined,
      brand_company_id: row.brand_company_id ?? undefined,
      owner_company_id: row.owner_company_id ?? undefined,
      owner_user_id: row.owner_user_id ?? undefined,
      first_name: row.first_name,
      last_name: row.last_name,
      email: row.email,
      phone: row.phone,
      whatsapp: row.whatsapp,
      job_title: row.job_title,
      city: row.city,
      relationship_type: row.relationship_type ?? '',
      notes: row.notes ?? '',
      custom: { ...row.custom }
    };
    formError = '';
    showForm = true;
  }

  function closeForm() {
    showForm = false;
  }

  function submit(e: SubmitEvent) {
    e.preventDefault();
    if (!form.first_name.trim() && !form.last_name.trim()) {
      formError = 'First or last name is required';
      return;
    }
    formError = '';
    // The three hierarchy fields are always sent as an explicit number or
    // null, never omitted: JSON.stringify drops `undefined`-valued keys
    // entirely, and the backend's partial-update convention (getInt64Ptr)
    // treats an ABSENT key as "leave the existing DB value untouched" —
    // so an empty/cleared field here (`undefined`, e.g. after the vertical-
    // switch guard above, or the picker's own "-- none --" clear) must cross
    // the wire as null to actually clear server-side, not just silently
    // vanish from the request and leave a stale link in place.
    const body: Record<string, unknown> = {
      ...form,
      hotel_company_id: form.hotel_company_id ?? null,
      brand_company_id: form.brand_company_id ?? null,
      owner_company_id: form.owner_company_id ?? null
    };
    // Ownership changes must use the audited transfer endpoint, never generic PUT.
    if (editingId || !(session.isAdmin || session.me?.user.intake_coordinator)) delete body.owner_user_id;
    saveMutation.mutate(body);
  }

  // Consumes the Header's "+ New person" / topbar-search-result signal (see
  // session.svelte.ts's PageSignal doc).
  $effect(() => {
    const signal = session.consumeSignal('people');
    if (!signal) return;
    if (signal.mode === 'create') openCreate();
    else openHistory(signal.record as Person);
  });

  function remove(row: Person) {
    scheduleUndo(`Delete “${row.first_name} ${row.last_name}”`, () => deleteMutation.mutate(row.id));
  }

  // ===================== Attach to site =====================
  // Compact counterpart to the full Edit form's Hotel/Brand/Group fields —
  // one picker, opened straight from an unlinked row, for the "just fix this
  // one contact" case rather than the whole edit form.
  let attachRow = $state<Person | null>(null);
  let attachForm = $state({
    hotel_company_id: undefined as number | undefined,
    brand_company_id: undefined as number | undefined,
    owner_company_id: undefined as number | undefined
  });
  let attachError = $state('');

  function openAttach(row: Person) {
    attachForm = {
      hotel_company_id: row.hotel_company_id ?? undefined,
      brand_company_id: row.brand_company_id ?? undefined,
      owner_company_id: row.owner_company_id ?? undefined
    };
    attachError = '';
    attachRow = row;
  }

  function onAttachHotelSelect(c: CompanyLookup) {
    if (attachForm.brand_company_id === undefined && c.brand_company_id) {
      attachForm.brand_company_id = c.brand_company_id;
    }
    if (attachForm.owner_company_id === undefined && c.owner_company_id) {
      attachForm.owner_company_id = c.owner_company_id;
    }
  }

  const attachMutation = createMutation(() => ({
    mutationFn: () =>
      peopleApi.update(attachRow!.id, {
        hotel_company_id: attachForm.hotel_company_id ?? null,
        brand_company_id: attachForm.brand_company_id ?? null,
        owner_company_id: attachForm.owner_company_id ?? null
      }),
    onSuccess: () => {
      attachRow = null;
      queryClient.invalidateQueries({ queryKey: ['people'] });
      queryClient.invalidateQueries({ queryKey: ['site-people'] });
      queryClient.invalidateQueries({ queryKey: ['site-activities'] });
    },
    onError: (err: unknown) => (attachError = getErrorMessage(err))
  }));

  function submitAttach(e: SubmitEvent) {
    e.preventDefault();
    if (!attachForm.hotel_company_id) {
      attachError = 'Pick or add the site (hotel/property)';
      return;
    }
    attachError = '';
    attachMutation.mutate();
  }

  function companyName(id: number | null): string {
    if (!id) return '';
    return companiesQuery.data?.results.find((c) => c.id === id)?.name ?? '';
  }

  function initials(row: Person): string {
    return `${row.first_name?.[0] ?? ''}${row.last_name?.[0] ?? ''}`.toUpperCase() || '?';
  }
</script>

<div class="tym-page">
  <CoordinatorWorkspace />
  {#if recordQuery.isError}<p class="tym-error-state">{getErrorMessage(recordQuery.error)} <button type="button" class="btn-secondary" onclick={() => recordQuery.refetch()}>Retry</button></p>{/if}
  <div class="mb-4 space-y-1">
    <p class="text-[12.5px] text-dark-gray">{listQuery.data?.count ?? 0} contacts</p>
    <p class="text-sm text-dark-gray">Open a contact to see their history, log a conversation, or plan your next step.</p>
  </div>

  {#if trackSuggestion}
    <div class="tym-card mb-4 flex flex-wrap items-center gap-3 px-4 py-3">
      <Globe class="h-4 w-4 shrink-0 text-toyama-teal" />
      <span class="flex-1 text-[12.5px] text-navy">
        Track <span class="font-semibold">@{trackSuggestion.domain}</span> for
        <span class="font-semibold">{trackSuggestion.companyName}</span>?
      </span>
      {#if trackSuggestionError}<span class="text-[12px] text-red-600">{trackSuggestionError}</span>{/if}
      <button
        type="button"
        class="btn-primary !h-9 !px-3 !text-[12.5px]"
        onclick={() => trackDomainMutation.mutate()}
        disabled={trackDomainMutation.isPending}
      >
        {trackDomainMutation.isPending ? 'Tracking...' : 'Track'}
      </button>
      <button type="button" class="btn-secondary !h-9 !px-3 !text-[12.5px]" onclick={() => (trackSuggestion = null)}>
        Dismiss
      </button>
    </div>
  {/if}

  <div class="flex flex-wrap items-start gap-3">
    <div class="min-w-0 flex-1">
      <SearchBar bind:value={search} onCreate={openCreate} createLabel="New contact" />
    </div>
    <select class="input !w-auto" aria-label="Contact type" bind:value={category}>
      <option value="">All contact types</option>
      {#each categories as [value, label]}<option {value}>{label}</option>{/each}
    </select>
    <select class="input !w-auto" aria-label="Contact ownership" bind:value={ownerFilter}>
      <option value="">All contact owners</option>
      <option value="me">My contacts</option>
      <option value="unassigned">Unassigned</option>
      {#if verticalId !== undefined}
        {#each filterOwnersQuery.data?.results ?? [] as owner (owner.id)}
          <option value={String(owner.id)}>{owner.name || owner.email}</option>
        {/each}
      {/if}
    </select>
    {#if verticalId !== undefined && filterOwnersQuery.isError}
      <p role="alert" class="text-xs text-red-600">Could not load named contact owners: {getErrorMessage(filterOwnersQuery.error)} <button type="button" class="underline" onclick={() => filterOwnersQuery.refetch()}>Retry owners</button></p>
    {/if}
    {#if listIsHotels}
      <button
        type="button"
        class="tym-chip shrink-0 whitespace-nowrap {unlinkedOnly ? 'active' : ''}"
        onclick={() => (unlinkedOnly = !unlinkedOnly)}
        title="Contacts with no hotel/company link"
      >
        Unlinked only
      </button>
    {/if}
    <button type="button" class="btn-secondary shrink-0" onclick={() => (showImport = true)} aria-label="Import">
      <Upload class="h-4 w-4" />
      <span class="hidden sm:inline">Import</span>
    </button>
  </div>

  {#if listQuery.isLoading}
    <QueryStatus loading label="people" />
  {:else if listQuery.isError}
    <QueryStatus error={listQuery.error} label="people" onRetry={() => listQuery.refetch()} />
  {:else if listQuery.data}
    <!-- desktop/tablet table; below sm it gives way to the card list -->
    <div class="tym-table-wrap tym-shadow hidden sm:block">
      <div class="tym-table-scroll">
        <table class="tym-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Job title</th>
              <th>Contact type</th>
              <th>Contact owner</th>
              <th>Last touchpoint</th>
              <th>Next action / date</th>
              {#if listIsHotels}
                <th>Hotel</th>
                <th>Brand</th>
                <th>Group</th>
              {:else}
                <th>Company</th>
              {/if}
              <th>Contact</th>
              <th class="num"></th>
            </tr>
          </thead>
          <tbody>
            {#each listQuery.data.results as row (row.id)}
              <tr class="clickable" onclick={() => openHistory(row)}>
                <td>
                  <div class="flex items-center gap-[11px]">
                    <span
                      class="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full font-heading text-[12px] font-bold"
                      style="background:#E3F0F1; color:#226A73"
                    >
                      {initials(row)}
                    </span>
                    <button type="button" class="text-left text-[13.5px] font-semibold text-navy underline decoration-dotted underline-offset-4" onclick={(e) => { e.stopPropagation(); openHistory(row); }}>{row.first_name} {row.last_name}</button>
                  </div>
                </td>
                <td class="muted">{row.job_title}</td>
                <td>{categoryLabel(row.relationship_type)}</td>
                <td>{contactOwnerLabel(row)}</td>
                <td>
                  {#if row.last_touchpoint}
                    <div>{interactionLabel(row.last_touchpoint.type)} · {dateLabel(row.last_touchpoint.touchpoint_at ?? row.last_touchpoint.created_at)}</div>
                    <div class="text-[12px] text-dark-gray">{row.last_touchpoint.outcome || row.last_touchpoint.title}</div>
                  {:else}<span class="text-xs text-dark-gray">No interactions yet</span>{/if}
                </td>
                <td>
                  {#if row.next_followup}
                    <div class="rounded-lg border px-3 py-2 {followupClass(row.next_followup.next_action_date)}">
                      <div class="font-semibold">{row.next_followup.next_action || 'Follow up'}</div>
                      <div class="text-[12px]">{followupDateLabel(row.next_followup.next_action_date)}</div>
                    </div>
                  {:else}<span class="text-xs text-dark-gray">No next step planned</span>{/if}
                  <button type="button" class="text-toyama-teal underline" onclick={(e) => { e.stopPropagation(); openHistory(row); }}>History / log interaction</button>
                </td>
                {#if listIsHotels}
                  <td>
                    {#if row.hotel_name}
                      {row.hotel_name}
                    {:else}
                      <span class="tym-badge" style="background:#FBEAEA; color:#9c3b3b">Unlinked</span>
                    {/if}
                  </td>
                  <td>{row.brand_name ?? '—'}</td>
                  <td>{row.owner_name ?? '—'}</td>
                {:else}
                  <td>{companyName(row.company_id)}</td>
                {/if}
                <td class="muted">
                  <div>{row.email}</div>
                  {#if row.phone}<div class="text-[12px] text-dark-gray">{row.phone}</div>{/if}
                </td>
                <td class="num">
                  <div class="flex justify-end gap-1.5">
                    {#if listIsHotels && !row.hotel_company_id}
                      <button
                        type="button"
                        class="btn-secondary !h-10 !w-10 !p-0"
                        onclick={(e) => {
                          e.stopPropagation();
                          openAttach(row);
                        }}
                        title="Attach to site"
                        aria-label={`Attach ${row.first_name} ${row.last_name} to a site`}
                      >
                        <MapPin class="h-4 w-4" />
                      </button>
                    {/if}
                    <button
                      type="button"
                      class="btn-danger !h-10 !w-10 !p-0"
                      onclick={(e) => {
                        e.stopPropagation();
                        remove(row);
                      }}
                      aria-label={`Delete ${row.first_name} ${row.last_name}`}
                    >
                      <Trash2 class="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            {:else}
              <tr><td class="empty" colspan={listIsHotels ? 11 : 9}>{search || category || ownerFilter || unlinkedOnly ? 'No contacts match these filters. Try another search or contact type.' : 'No contacts yet. Add a contact to start building your relationship history.'}</td></tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>

    <!-- mobile card list — the wide contact table is unreadable at 390px -->
    <div class="flex flex-col gap-2.5 sm:hidden">
      {#each listQuery.data.results as row (row.id)}
        <button type="button" class="tym-list-card" onclick={() => openHistory(row)}>
          <span class="flex items-center gap-3">
            <span
              class="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full font-heading text-[12px] font-bold"
              style="background:#E3F0F1; color:#226A73"
            >
              {initials(row)}
            </span>
            <span class="min-w-0 flex-1">
              <span class="block truncate text-[14.5px] font-semibold text-navy"
                >{row.first_name} {row.last_name}</span
              >
              <span class="mt-0.5 block truncate text-[11.5px] text-dark-gray">
                {#if row.job_title}{row.job_title}{/if}{#if row.job_title && (listIsHotels ? row.hotel_name : companyName(row.company_id))}
                  &middot;
                {/if}{#if listIsHotels}{row.hotel_name ?? 'Unlinked'}{:else}{companyName(row.company_id)}{/if}
              </span>
              {#if row.email || row.phone}
                <span class="mt-0.5 block truncate text-[11.5px] text-[#666]">{row.email || row.phone}</span>
              {/if}
              <span class="mt-1 block text-xs text-dark-gray">Contact owner: {contactOwnerLabel(row)}</span>
              <span class="mt-1 block text-[11.5px] text-dark-gray">{categoryLabel(row.relationship_type)} · Last: {row.last_touchpoint ? `${interactionLabel(row.last_touchpoint.type)} ${dateLabel(row.last_touchpoint.touchpoint_at ?? row.last_touchpoint.created_at)}` : 'No interactions yet'}</span>
              {#if row.next_followup}
                <span class="mt-2 block rounded-lg border p-2 text-xs {followupClass(row.next_followup.next_action_date)}"><span class="block font-semibold">{row.next_followup.next_action || 'Follow up'}</span>{followupDateLabel(row.next_followup.next_action_date)}</span>
              {:else}<span class="mt-1 block text-xs text-dark-gray">No next step planned</span>{/if}
              <span class="mt-2 block text-xs font-semibold text-toyama-teal">View history / log interaction →</span>
            </span>
            <ChevronRight class="h-4 w-4 shrink-0 text-dark-gray" />
          </span>
        </button>
      {:else}
        <div class="tym-card px-4 py-8 text-center text-[13px] text-dark-gray">{search || category || unlinkedOnly ? 'No contacts match these filters. Try another search or contact type.' : 'No contacts yet. Add a contact to start building your relationship history.'}</div>
      {/each}
    </div>

    <Pagination
      page={listQuery.data.page}
      perPage={listQuery.data.per_page}
      count={listQuery.data.count}
      onChange={(p) => (page = p)}
    />
  {/if}
</div>

{#if showForm}
  <Modal title={editingId ? 'Edit contact' : 'New contact'} onClose={closeForm}>
    <form id="person-form" class="space-y-4" onsubmit={submit}>
      <h3 class="font-semibold text-navy">Contact details</h3>
      {#if !editingId}
        <p class="text-sm text-dark-gray">Sales contacts automatically belong to their pipeline's configured salesperson: Khudoos, Ahad or Dastageer. The creator and meeting coordinator are separate responsibilities. Other teams retain their existing creator defaults.</p>
        {#if session.isAdmin || session.me?.user.intake_coordinator}
          <label class="label" for="create-contact-owner">Initial contact owner</label>
          <select id="create-contact-owner" class="input" bind:value={form.owner_user_id}>
            <option value={undefined}>Automatic pipeline owner</option>
            {#each createOwnersQuery.data?.results ?? [] as user}
              <option value={user.id}>{user.name || user.email}</option>
            {/each}
          </select>
          {#if createOwnersQuery.isError}<p role="alert" class="text-sm text-red-600">{getErrorMessage(createOwnersQuery.error)}</p><button type="button" class="btn-secondary" onclick={() => createOwnersQuery.refetch()}>Retry owners</button>{/if}
        {/if}
      {:else}<p class="text-sm text-dark-gray">Change ownership or pipeline through Assign / transfer on the contact, with a handover note.</p>{/if}
      {#if session.me && session.me.verticals.length > 1}
        <div>
          <label class="label" for="vertical">Vertical</label>
          <select id="vertical" class="input" bind:value={form.vertical_id} disabled={!!editingId} onchange={() => form.owner_user_id = undefined}>
            {#each session.me.verticals as v (v.id)}
              <option value={v.id}>{v.name}</option>
            {/each}
          </select>
        </div>
      {/if}
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label class="label" for="first_name">First name</label>
          <input id="first_name" class="input" placeholder="First name" bind:value={form.first_name} />
        </div>
        <div>
          <label class="label" for="last_name">Last name</label>
          <input id="last_name" class="input" placeholder="Last name" bind:value={form.last_name} />
        </div>
      </div>
      {#if formIsHotels}
        <div>
          <label class="label" for="hotel">Hotel</label>
          <CompanyCombobox
            id="hotel"
            bind:value={form.hotel_company_id}
            companies={hotelsPoolQuery.data?.results ?? []}
            verticalId={form.vertical_id}
            kind="hotel"
            onSelect={onHotelSelect}
          />
        </div>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label class="label" for="brand">Brand</label>
            <CompanyCombobox
              id="brand"
              bind:value={form.brand_company_id}
              companies={brandsPoolQuery.data?.results ?? []}
              verticalId={form.vertical_id}
              kind="brand"
            />
          </div>
          <div>
            <label class="label" for="group">Group</label>
            <CompanyCombobox
              id="group"
              bind:value={form.owner_company_id}
              companies={ownersPoolQuery.data?.results ?? []}
              verticalId={form.vertical_id}
              kind="owner"
            />
          </div>
        </div>
      {:else}
        <div>
          <label class="label" for="company">Company</label>
          <CompanyCombobox
            id="company"
            bind:value={form.company_id}
            companies={companiesQuery.data?.results ?? []}
            verticalId={form.vertical_id}
          />
        </div>
      {/if}
      <div>
        <label class="label" for="job_title">Job title</label>
        <input id="job_title" class="input" placeholder="e.g. Principal architect" bind:value={form.job_title} />
      </div>
      <div>
        <label class="label" for="relationship_type">Contact type</label>
        <select id="relationship_type" class="input" bind:value={form.relationship_type}>
          <option value="">Choose a type (optional)</option>
          {#each categories as [value, label]}<option {value}>{label}</option>{/each}
        </select>
      </div>
      <div>
        <label class="label" for="person-notes">About this contact</label>
        <textarea id="person-notes" class="input" rows="2" placeholder="Preferences or background. Log conversations in contact history." bind:value={form.notes}></textarea>
      </div>
      <h3 class="border-t border-gray-200 pt-3 font-semibold text-navy">How to reach them</h3>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label class="label" for="email">Email</label>
          <input id="email" type="email" class="input" placeholder="name@company.com" bind:value={form.email} />
        </div>
        <div>
          <label class="label" for="phone">Phone</label>
          <input id="phone" class="input" placeholder="Phone number" bind:value={form.phone} />
        </div>
      </div>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label class="label" for="whatsapp">WhatsApp</label>
          <input id="whatsapp" class="input" placeholder="WhatsApp number" bind:value={form.whatsapp} />
        </div>
        <div>
          <label class="label" for="city">City</label>
          <input id="city" class="input" placeholder="City" bind:value={form.city} />
        </div>
      </div>

      <CustomFieldsForm defs={customFieldDefs} values={form.custom} />

      {#if formError}<p class="text-sm text-red-600">{formError}</p>{/if}
    </form>

    {#snippet footer()}
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-secondary" onclick={closeForm}>Cancel</button>
        <!-- sibling of the <form>, so it submits via the `form` attribute -->
        <button type="submit" form="person-form" class="btn-primary" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    {/snippet}
  </Modal>
{/if}
{#if showImport}
  <ImportWizard onClose={() => (showImport = false)} />
{/if}
{#if detailPerson}
  <Modal title={`${detailPerson.first_name} ${detailPerson.last_name}`} onClose={closeHistory}>
    {#key detailPerson.id}<ContactOwnership person={detailPerson} onTransferred={contactTransferred} />{/key}
    <div class="mb-5 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p class="font-semibold text-navy">{categoryLabel(detailPerson.relationship_type)}{detailPerson.job_title ? ` · ${detailPerson.job_title}` : ''}</p>
          <p class="text-dark-gray">{[detailPerson.hotel_name || companyName(detailPerson.company_id), detailPerson.brand_name, detailPerson.owner_name, detailPerson.city].filter(Boolean).join(' · ') || 'No company or location added'}</p>
        </div>
        <button type="button" class="btn-secondary" onclick={() => { const person = detailPerson; historyPerson = null; if (person) openEdit(person); }}>Edit contact</button>
      </div>
      <div class="flex flex-wrap gap-x-4 gap-y-1">
        {#if detailPerson.email}<a class="break-all text-toyama-teal underline" href={`mailto:${detailPerson.email}`}>{detailPerson.email}</a>{/if}
        {#if detailPerson.phone}<a class="text-toyama-teal underline" href={`tel:${detailPerson.phone}`}>{detailPerson.phone}</a>{/if}
        {#if detailPerson.whatsapp}<span>WhatsApp: {detailPerson.whatsapp}</span>{/if}
        {#if !detailPerson.email && !detailPerson.phone && !detailPerson.whatsapp}<p class="text-dark-gray">No contact details yet. Choose Edit contact to add them.</p>{/if}
      </div>
      {#if detailPerson.notes}<div><p class="text-xs font-semibold text-dark-gray">About this contact</p><p class="whitespace-pre-wrap">{detailPerson.notes}</p></div>{/if}
      {#if detailPerson.next_followup}
        <div class="rounded-lg border p-3 {followupClass(detailPerson.next_followup.next_action_date)}">
          <p class="text-xs font-semibold">Next step</p>
          <p class="font-semibold">{detailPerson.next_followup.next_action || 'Follow up'}</p>
          <p class="text-xs">{followupDateLabel(detailPerson.next_followup.next_action_date)}</p>
          <p class="mt-1 text-xs">Update this next step on its interaction below.</p>
        </div>
      {:else}<p class="text-xs text-dark-gray">No next step planned. Log an interaction below to plan your follow-up.</p>{/if}
    </div>
    {#key detailPerson.id}
      <ContactHistory personId={detailPerson.id} verticalId={detailPerson.vertical_id} companyId={detailPerson.company_id} contactOwnerUserId={detailPerson.owner_user_id} />
    {/key}
  </Modal>
{/if}
{#if attachRow}
  <Modal title="Attach to site" onClose={() => (attachRow = null)}>
    <form id="attach-site-form" class="space-y-4" onsubmit={submitAttach}>
      <p class="text-[12.5px] text-dark-gray">
        Link <span class="font-semibold text-navy">{attachRow.first_name} {attachRow.last_name}</span> to a property —
        pick an existing one or add a new one.
      </p>
      <div>
        <label class="label" for="attach-hotel">Site (hotel/property)</label>
        <CompanyCombobox
          id="attach-hotel"
          bind:value={attachForm.hotel_company_id}
          verticalId={attachRow.vertical_id}
          kind="hotel"
          onSelect={onAttachHotelSelect}
          placeholder="Search or add a property…"
        />
      </div>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label class="label" for="attach-brand">Brand</label>
          <CompanyCombobox
            id="attach-brand"
            bind:value={attachForm.brand_company_id}
            verticalId={attachRow.vertical_id}
            kind="brand"
          />
        </div>
        <div>
          <label class="label" for="attach-owner">Group</label>
          <CompanyCombobox
            id="attach-owner"
            bind:value={attachForm.owner_company_id}
            verticalId={attachRow.vertical_id}
            kind="owner"
          />
        </div>
      </div>
      {#if attachError}<p class="text-sm text-red-600">{attachError}</p>{/if}
    </form>

    {#snippet footer()}
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-secondary" onclick={() => (attachRow = null)}>Cancel</button>
        <!-- sibling of the <form>, so it submits via the `form` attribute -->
        <button type="submit" form="attach-site-form" class="btn-primary" disabled={attachMutation.isPending}>
          {attachMutation.isPending ? 'Attaching...' : 'Attach'}
        </button>
      </div>
    {/snippet}
  </Modal>
{/if}
